import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L, { Layer } from 'leaflet';
import MapDraw from 'components/map/map-draw/map-draw';
import { includeColor } from 'utils/geometry';

export type SelectionMode = 'click' | 'lasso' | 'department';

// leaflet-geoman toolbar button, not fully typed by the library
type ToolbarButton = L.Control & {
  toggled: () => boolean;
  toggle: (status: boolean) => void;
};

const getButton = (map: L.Map, name: 'cities' | 'departments') =>
  map.pm.Toolbar.getButton(name) as ToolbarButton | undefined;

type InterventionsFieldToolbarProps = {
  selectionMode: SelectionMode;
  canDraw: boolean;
  onChangeSelectionMode: (selectionMode: SelectionMode) => void;
  onPolygonDrawed: (e: { shape: string; layer: Layer }) => void;
};

// leaflet-geoman toolbar: the drawing buttons when no city is selected,
// otherwise the cities selection button, and always the departments one
const InterventionsFieldToolbar: FunctionComponent<
  InterventionsFieldToolbarProps
> = ({ selectionMode, canDraw, onChangeSelectionMode, onPolygonDrawed }) => {
  const map = useMap();

  // The toolbar callbacks are bound once, keep the latest one
  const onButtonClickRef = useRef<(name: 'cities' | 'departments') => void>();
  onButtonClickRef.current = (name) => {
    if (name === 'cities') {
      // Clicking the active button untoggles it, keep it toggled
      getButton(map, 'cities')?.toggle(true);
      onChangeSelectionMode('click');
      return;
    }

    if (getButton(map, 'departments')?.toggled()) {
      map.pm.disableDraw();
      onChangeSelectionMode('department');
      return;
    }

    onChangeSelectionMode(canDraw ? 'lasso' : 'click');
  };

  useEffect(() => {
    // Leave the department selection when a drawing starts
    const onDrawStart = () => onChangeSelectionMode('lasso');
    map.on('pm:drawstart', onDrawStart);

    return () => {
      map.off('pm:drawstart', onDrawStart);
    };
  }, [map, onChangeSelectionMode]);

  useEffect(() => {
    map.pm.addControls({ circle: canDraw, include: canDraw, cities: !canDraw });
    getButton(map, 'cities')?.toggle(selectionMode === 'click');
    getButton(map, 'departments')?.toggle(selectionMode === 'department');
  }, [map, canDraw, selectionMode]);

  return (
    <MapDraw
      options={{
        position: 'bottomright',
        editControls: false,
        drawPolygon: false,
        rotateMode: false,
        cutPolygon: false,
      }}
      add={(map) => {
        // The buttons are kept by the map if the toolbar is added again
        if (map.pm.Toolbar.controlExists('circle')) return;

        map.pm.Toolbar.copyDrawControl('Circle', {
          name: 'circle',
          title: 'Dessiner un cercle',
          block: 'options',
          toggle: true,
          className: 'polygon-circle',
        });
        map.pm.Toolbar.copyDrawControl('Polygon', {
          name: 'include',
          title: 'Dessiner une zone',
          block: 'options',
          toggle: true,
          className: 'include',
        });
        map.pm.Toolbar.createCustomControl({
          name: 'cities',
          title: 'Sélectionner des villes',
          block: 'options',
          toggle: true,
          className: 'cities',
          // The selection buttons are toggled from the selection mode only
          ...{ disableByOtherButtons: false },
          afterClick: () => onButtonClickRef.current?.('cities'),
        });
        map.pm.Toolbar.createCustomControl({
          name: 'departments',
          title: 'Sélectionner des départements',
          block: 'options',
          toggle: true,
          className: 'departments',
          ...{ disableByOtherButtons: false },
          afterClick: () => onButtonClickRef.current?.('departments'),
        });
        map.pm.Draw['circle'].setOptions({
          hintlineStyle: { color: includeColor, dashArray: [5, 5] },
          templineStyle: { color: includeColor },
        });
        map.pm.Draw['include'].setOptions({
          hintlineStyle: { color: includeColor, dashArray: [5, 5] },
          templineStyle: { color: includeColor },
        });
      }}
      onCreate={onPolygonDrawed}
    />
  );
};

export default InterventionsFieldToolbar;
